import { defineStore } from 'pinia';
import axios from '@/api/config/axios';

export const useFinanceiroStore = defineStore('financeiroStore', {
    state: () => ({
        dados: {
            receita_atual: 0,
            receita_anterior: 0,
            inscricoes_atual: 0,
            inscricoes_anterior: 0,
            ticket_atual: 0,
            ticket_anterior: 0,
            reembolso_atual: 0,
            reembolso_anterior: 0
        },
        saldo: {
            available_balance: 0,
            pending_balance: 0,
            total_balance: 0,
            last_audit_at: null
        },
        loading: false,
        loadingSaldo: false,
        error: null,
    }),

    getters: {
        getDados: (state) => state.dados,
        getLoading: (state) => state.loading,
        getError: (state) => state.error,
        
        // Calcula as porcentagens de variação
        getVariacaoReceita: (state) => {
            if (state.dados.receita_anterior === 0) {
                return state.dados.receita_atual > 0 ? 100 : 0;
            }
            return ((state.dados.receita_atual - state.dados.receita_anterior) / state.dados.receita_anterior) * 100;
        },
        
        getVariacaoInscricoes: (state) => {
            if (state.dados.inscricoes_anterior === 0) {
                return state.dados.inscricoes_atual > 0 ? 100 : 0;
            }
            return ((state.dados.inscricoes_atual - state.dados.inscricoes_anterior) / state.dados.inscricoes_anterior) * 100;
        },
        
        getVariacaoTicket: (state) => {
            if (state.dados.ticket_anterior === 0) {
                return state.dados.ticket_atual > 0 ? 100 : 0;
            }
            return ((state.dados.ticket_atual - state.dados.ticket_anterior) / state.dados.ticket_anterior) * 100;
        },
        
        getVariacaoReembolso: (state) => {
            if (state.dados.reembolso_anterior === 0) {
                return state.dados.reembolso_atual > 0 ? 100 : 0;
            }
            return ((state.dados.reembolso_atual - state.dados.reembolso_anterior) / state.dados.reembolso_anterior) * 100;
        },
        
        // Retorna as cores baseadas na variação (positivo = verde, negativo = vermelho)
        // Para reembolso: positivo = vermelho, negativo = verde
        getCorReceita: (state) => {
            if (state.dados.receita_anterior === 0) {
                return state.dados.receita_atual > 0 ? 'text-success' : 'text-muted';
            }
            const variacao = ((state.dados.receita_atual - state.dados.receita_anterior) / state.dados.receita_anterior) * 100;
            return variacao >= 0 ? 'text-success' : 'text-danger';
        },
        
        getCorInscricoes: (state) => {
            if (state.dados.inscricoes_anterior === 0) {
                return state.dados.inscricoes_atual > 0 ? 'text-success' : 'text-muted';
            }
            const variacao = ((state.dados.inscricoes_atual - state.dados.inscricoes_anterior) / state.dados.inscricoes_anterior) * 100;
            return variacao >= 0 ? 'text-success' : 'text-danger';
        },
        
        getCorTicket: (state) => {
            if (state.dados.ticket_anterior === 0) {
                return state.dados.ticket_atual > 0 ? 'text-success' : 'text-muted';
            }
            const variacao = ((state.dados.ticket_atual - state.dados.ticket_anterior) / state.dados.ticket_anterior) * 100;
            return variacao >= 0 ? 'text-success' : 'text-danger';
        },
        
        getCorReembolso: (state) => {
            if (state.dados.reembolso_anterior === 0) {
                return state.dados.reembolso_atual > 0 ? 'text-danger' : 'text-success';
            }
            const variacao = ((state.dados.reembolso_atual - state.dados.reembolso_anterior) / state.dados.reembolso_anterior) * 100;
            return variacao >= 0 ? 'text-danger' : 'text-success';
        },
        
        getCorReembolsoBadge: (state) => {
            if (state.dados.reembolso_anterior === 0) {
                return state.dados.reembolso_atual > 0 ? 'bg-danger' : 'bg-success';
            }
            const variacao = ((state.dados.reembolso_atual - state.dados.reembolso_anterior) / state.dados.reembolso_anterior) * 100;
            return variacao >= 0 ? 'bg-danger' : 'bg-success';
        },
        
        getCorReembolsoIcon: (state) => {
            if (state.dados.reembolso_anterior === 0) {
                return state.dados.reembolso_atual > 0 ? 'trending-up' : 'trending-down';
            }
            const variacao = ((state.dados.reembolso_atual - state.dados.reembolso_anterior) / state.dados.reembolso_anterior) * 100;
            return variacao >= 0 ? 'trending-up' : 'trending-down';
        },
        
        // Formata valores monetários
        getReceitaFormatada: (state) => {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(state.dados.receita_atual);
        },
        
        getReceitaAnteriorFormatada: (state) => {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(state.dados.receita_anterior);
        },
        
        getTicketFormatado: (state) => {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(state.dados.ticket_atual);
        },
        
        getTicketAnteriorFormatado: (state) => {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(state.dados.ticket_anterior);
        },
        
        // Formata porcentagens
        getVariacaoReceitaFormatada: (state) => {
            if (state.dados.receita_anterior === 0) {
                return state.dados.receita_atual > 0 ? '+100.0%' : '0.0%';
            }
            const variacao = ((state.dados.receita_atual - state.dados.receita_anterior) / state.dados.receita_anterior) * 100;
            return `${variacao >= 0 ? '+' : ''}${variacao.toFixed(1)}%`;
        },
        
        getVariacaoInscricoesFormatada: (state) => {
            if (state.dados.inscricoes_anterior === 0) {
                return state.dados.inscricoes_atual > 0 ? '+100.0%' : '0.0%';
            }
            const variacao = ((state.dados.inscricoes_atual - state.dados.inscricoes_anterior) / state.dados.inscricoes_anterior) * 100;
            return `${variacao >= 0 ? '+' : ''}${variacao.toFixed(1)}%`;
        },
        
        getVariacaoTicketFormatada: (state) => {
            if (state.dados.ticket_anterior === 0) {
                return state.dados.ticket_atual > 0 ? '+100.0%' : '0.0%';
            }
            const variacao = ((state.dados.ticket_atual - state.dados.ticket_anterior) / state.dados.ticket_anterior) * 100;
            return `${variacao >= 0 ? '+' : ''}${variacao.toFixed(1)}%`;
        },
        
        getVariacaoReembolsoFormatada: (state) => {
            if (state.dados.reembolso_anterior === 0) {
                return state.dados.reembolso_atual > 0 ? '+100.0%' : '0.0%';
            }
            const variacao = ((state.dados.reembolso_atual - state.dados.reembolso_anterior) / state.dados.reembolso_anterior) * 100;
            return `${variacao >= 0 ? '+' : ''}${variacao.toFixed(1)}%`;
        },
        
        // Getters para o saldo
        getSaldo: (state) => state.saldo,
        getLoadingSaldo: (state) => state.loadingSaldo,
        
        // Formata valores do saldo
        getSaldoDisponivelFormatado: (state) => {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(state.saldo.available_balance);
        },
        
        getSaldoPendenteFormatado: (state) => {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(state.saldo.pending_balance);
        },
        
        getSaldoTotalFormatado: (state) => {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(state.saldo.gross_revenue_monthly);
        },
        
        // Formata data da última auditoria
        getUltimaAuditoriaFormatada: (state) => {
            if (!state.saldo.last_audit_at) return 'Nunca';
            
            return new Date(state.saldo.last_audit_at).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    },

    actions: {
        async fetchDadosFinanceiros() {
            this.loading = true;
            this.error = null;
            
            try {
                const response = await axios.get('/api/v1/financeiro/dados');
                
                if (response.data.success) {
                    this.dados = response.data.data;
                } else {
                    this.error = response.data.message || 'Erro ao obter dados financeiros';
                }
            } catch (error) {
                console.error('Erro ao buscar dados financeiros:', error);
                this.error = error.response?.data?.message || 'Erro ao conectar com o servidor';
                
                // Em caso de erro, definir valores padrão
                this.dados = {
                    receita_atual: 0,
                    receita_anterior: 0,
                    inscricoes_atual: 0,
                    inscricoes_anterior: 0,
                    ticket_atual: 0,
                    ticket_anterior: 0,
                    reembolso_atual: 0,
                    reembolso_anterior: 0
                };
            } finally {
                this.loading = false;
            }
        },
        
        clearError() {
            this.error = null;
        },
        
        async fetchSaldo() {
            this.loadingSaldo = true;
            this.error = null;
            
            try {
                const response = await axios.get('/api/v1/financial/balance');
                
                if (response.data.success) {
                    this.saldo = response.data.data;
                } else {
                    this.error = response.data.message || 'Erro ao obter saldo';
                }
            } catch (error) {
                console.error('Erro ao buscar saldo:', error);
                this.error = error.response?.data?.message || 'Erro ao conectar com o servidor';
                
                // Em caso de erro, definir valores padrão
                this.saldo = {
                    available_balance: 0,
                    pending_balance: 0,
                    total_balance: 0,
                    last_audit_at: null
                };
            } finally {
                this.loadingSaldo = false;
            }
        }
    }
});
